import { useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet, Loader2, CheckCircle2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { api, formatApiError } from "@/lib/api";

export const MainSheetImport = () => {
  const { user } = useAuth();
  const { reload } = useSettings();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  if (!["admin", "owner"].includes(user?.role)) return null;

  const upload = async (file) => {
    if (!file) return;
    if (!window.confirm("Import the City Removalist main sheet? Any previous main-sheet import will be replaced (jobs you created by hand are kept).")) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/bookings/import/main-sheet", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 300000 });
      setResult(data);
      reload?.();
      toast.success(`${data.imported} jobs imported from the main sheet`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const removeAll = async () => {
    if (!window.confirm("Remove ALL jobs that came from the main sheet import? Jobs you entered by hand are kept. This cannot be undone.")) return;
    setBusy(true);
    try {
      const { data } = await api.delete("/bookings/import/main-sheet");
      setResult(null);
      reload?.();
      toast.success(`${data.removed} imported jobs removed`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-6 rounded-xl border border-[#0E1E38]/20 bg-[#0E1E38] p-5 text-white" data-testid="main-sheet-import">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2 font-display text-base font-semibold"><FileSpreadsheet className="h-5 w-5 text-[#FACC15]" /> One-click import: "City Removalist – Main Sheet.xlsx"</div>
          <p className="mt-1 max-w-2xl text-sm text-sky-100/80">Reads the <b>Booking</b> tab directly – red rows → Cancelled, past jobs → Paid, future → Confirmed, partner companies merged under one name, hourly / share-load pricing rebuilt, missing info flagged.</p>
        </div>
        <div className="flex flex-col gap-2 sm:items-end">
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-md bg-[#FACC15] px-4 py-2 text-sm font-semibold text-[#0E1E38] hover:bg-[#EAB308]" data-testid="main-sheet-upload-label">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />} {busy ? "Working… (about 30s)" : "Upload main sheet"}
            <input type="file" accept=".xlsx,.xlsm" className="hidden" disabled={busy} onChange={(e) => { upload(e.target.files[0]); e.target.value = ""; }} data-testid="main-sheet-file-input" />
          </label>
          <button type="button" onClick={removeAll} disabled={busy} className="inline-flex items-center gap-1.5 text-xs font-medium text-rose-200 hover:text-rose-100 hover:underline disabled:opacity-50" data-testid="main-sheet-remove-btn"><Trash2 className="h-3.5 w-3.5" /> Remove all imported jobs</button>
        </div>
      </div>
      {result && (
        <div className="mt-4 grid gap-2 rounded-lg bg-white/10 p-4 text-sm sm:grid-cols-2 lg:grid-cols-4" data-testid="main-sheet-result">
          <div><CheckCircle2 className="mr-1 inline h-4 w-4 text-emerald-300" /><b>{result.imported}</b> jobs imported{result.replaced ? ` (replaced ${result.replaced})` : ""}</div>
          <div>{Object.entries(result.by_status).map(([s, n]) => `${s}: ${n}`).join(" · ")}</div>
          <div><b>{result.flagged}</b> flagged with missing info</div>
          <div className="truncate" title={result.companies.join(", ")}>{result.companies.length} partner companies</div>
          {result.errors?.length > 0 && <div className="sm:col-span-4 text-amber-200">{result.errors.length} rows could not be imported: {result.errors.map((e) => `row ${e.row}`).join(", ")}</div>}
        </div>
      )}
      {result && <Button variant="link" className="mt-2 h-auto p-0 text-xs text-sky-200" onClick={() => setResult(null)}>Clear</Button>}
    </div>
  );
};
